import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserFiles, clearUserFiles } from '../../slice/apiSlice';
import { BookOpen, File, Trash2Icon, Star } from 'lucide-react';
import { clearSources } from '../../slice/sourceSlice';
import { ReactComponent as FileIcon } from '../../assets/files.svg';
import { ReactComponent as TaskIcon } from '../../assets/tasks.svg';
import PDFViewer from './PDFViwer';
import constants, { BASE_URL } from '../constants';
import { useThemeColor } from '../../hooks/useThemeColor';
import SQ from '../atomic/SQ';
import { fetchSQByTaskId, selectSQ } from '../../slice/sqSlice';
import { fetchTaskByIdAsync, selectSelectedTask } from '../../slice/taskSlice';
import { getTaskStatusesByHistoryAsync } from '../../slice/taskStatusSlice';
import { uploadFilesWithHistory, createChatHistory } from '../../Api/req';
import { toast } from 'react-hot-toast';
import { fetchPDF } from '../../slice/pdfViewslice';
import { addSource } from '../../slice/sourceSlice';
import { useLocation } from 'react-router-dom';
import Button from '../atomic/Button';
import * as pdfjsLib from 'pdfjs-dist';
import {
  canStartTask,
  canUploadPages,
  recordTaskStarted,
  recordPagesUploaded,
  getUserOrganizationId,
} from '../../utils/usageChecker';

// Set the worker source for pdfjs-dist
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

function flashTitleElem(sanitizedTitle, attempts = 0) {
  const titleElem = document.getElementById(`chat-title-${sanitizedTitle}`);
  if (titleElem) {
    titleElem.classList.add('flash-highlight');
    setTimeout(() => {
      titleElem.classList.remove('flash-highlight');
    }, 1000);
  } else if (attempts < 10) {
    setTimeout(() => flashTitleElem(sanitizedTitle, attempts + 1), 100);
  }
}

function sanitizeTitleForId(title) {
  return title
    ? title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    : '';
}

function flashTitleElemWhenVisible(sanitizedTitle) {
  const titleId = `chat-title-${sanitizedTitle}`;
  let attempts = 0;
  const maxAttempts = 20;

  function tryFlash() {
    const titleElem = document.getElementById(titleId);
    if (titleElem) {
      const observer = new window.IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              titleElem.classList.add('flash-highlight');
              setTimeout(() => {
                titleElem.classList.remove('flash-highlight');
              }, 1000);
              obs.disconnect();
            }
          });
        },
        { threshold: 0.1 }
      );
      observer.observe(titleElem);
    } else if (attempts < maxAttempts) {
      attempts++;
      setTimeout(tryFlash, 100);
    }
  }
  tryFlash();
}

const ACCEPTED_FILE_TYPES = {
  'application/pdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-powerpoint': '.ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
  'text/plain': '.txt',
};

const Sidebar = ({ isOpen, onClose, historyId, taskID, taskName, onDescriptionSelect, onWidthChange, mode, setHistoryId }) => {
  const { organizations } = useSelector((state) => state.organizations);
  const { user } = useSelector((state) => state.user);
  const { data: userData } = useSelector((state) => state.admin);

  useEffect(() => {
    localStorage.setItem('pdfOpenedFromTab', 'files');
    localStorage.setItem('currentActiveTab', 'files');
    if (activeTab === 'files') {
      setActiveQuestionId(null);
      localStorage.removeItem('activeQuestionId');
    }

    const savedWidth = 445;
    const width = parseInt(savedWidth, 10);
    setSidebarWidth(width);

    const sidebarContainer = document.querySelector('.sidebar-container');
    if (sidebarContainer) {
      sidebarContainer.style.width = `${width}px`;
    }

    if (onWidthChange) {
      onWidthChange(width);
    }
  }, []);

  const [activeTab, setActiveTab] = useState('files');
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const [pdfZoomLevel, setPdfZoomLevel] = useState(1.0);
  const [isDragging, setIsDragging] = useState(false);
  const sidebarRef = useRef(null);
  const resizeHandleRef = useRef(null);
  const [activeQuestionId, setActiveQuestionId] = useState(() => {
    const savedTab = localStorage.getItem('pdfOpenedFromTab');
    const savedQuestionId = localStorage.getItem('activeQuestionId');
    const pdfOpenTimestamp = localStorage.getItem('pdfOpenTimestamp');
    const comingFromPdfView = pdfOpenTimestamp && savedQuestionId;
    return savedTab === 'tasks' && comingFromPdfView ? savedQuestionId : null;
  });
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [hasPendingFiles, setHasPendingFiles] = useState(true);
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPdfFullscreen, setIsPdfFullscreen] = useState(false);
  const fileInputRef = useRef(null);
  const questionCatalogRef = useRef(null);

  const state = useSelector((state) => state.history);
  const { chatHistory = [], status, error } = useSelector((state) => state.history);
  const hname = chatHistory?.history_records;
  const dispatch = useDispatch();
  const { userFiles, userFilesStatus, userFilesError } = useSelector((state) => state.api);
  const questions = useSelector(selectSQ);
  const taskStatuses = useSelector((state) => state.taskStatus.taskStatuses);
  const { aispaceColor } = useThemeColor();
  const { currentPdf, loading } = useSelector((state) => state.source);
  const location = useLocation();

  useEffect(() => {
    if (mode === 'chat') {
      setActiveTab('files');
      setActiveQuestionId(null);
      localStorage.removeItem('activeQuestionId');
      localStorage.setItem('pdfOpenedFromTab', 'files');
      localStorage.setItem('currentActiveTab', 'files');
    } else if (taskID) {
      dispatch(fetchSQByTaskId(taskID));
    }
  }, [taskID, dispatch, mode]);

  useEffect(() => {
    const pdfOpenTimestamp = localStorage.getItem('pdfOpenTimestamp');
    const initialPageLoad = !pdfOpenTimestamp;
    if (activeTab === 'files' || (initialPageLoad && activeTab === 'tasks')) {
      setActiveQuestionId(null);
      localStorage.removeItem('activeQuestionId');
    }
    if (activeQuestionId && activeTab === 'tasks') {
      scrollToActiveQuestion();
    }
  }, []);

  useEffect(() => {
    if (activeQuestionId && activeTab === 'tasks' && questionCatalogRef.current) {
      scrollToActiveQuestion();
    }
  }, [activeQuestionId, activeTab]);

  useEffect(() => {
    const handlePageLoad = () => {
      localStorage.removeItem('activeQuestionId');
      localStorage.removeItem('pdfSourceMessageId');
      localStorage.removeItem('pdfSourceMessageTitle');
      localStorage.removeItem('pdfOpenTimestamp');
      localStorage.removeItem('pdfOpenedFromTab');
      localStorage.removeItem('currentActiveTab');
      setActiveQuestionId(null);
    };

    handlePageLoad();
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        handlePageLoad();
      }
    });

    return () => {
      window.removeEventListener('pageshow', handlePageLoad);
    };
  }, []);

  useEffect(() => {
    if (historyId) {
      dispatch(getTaskStatusesByHistoryAsync(historyId));
    }
  }, [historyId, dispatch]);

  const userId = localStorage.getItem('id');

  useEffect(() => {
    if (currentPdf.blob) {
      const blobUrl = URL.createObjectURL(currentPdf.blob);
      setPdfBlobUrl(blobUrl);
      return () => URL.revokeObjectURL(blobUrl);
    }
  }, [currentPdf.blob]);

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const validateFile = (file) => {
    if (!file) return { isValid: false, error: 'No file selected' };
    const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
    const isValidType = Object.values(ACCEPTED_FILE_TYPES).includes(fileExtension);
    if (!isValidType) {
      return {
        isValid: false,
        error: `Please select only ${Object.values(ACCEPTED_FILE_TYPES).join(', ')} files`,
      };
    }
    return { isValid: true, error: null };
  };

  const handleFileChange = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    const validFiles = [];

    for (const file of selectedFiles) {
      const { isValid, error } = validateFile(file);
      if (isValid) {
        if (file.type === 'application/pdf') {
          try {
            const arrayBuffer = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
            file._pageCount = pdf.numPages; // Attach page count to file object
            validFiles.push(file);
            await pdf.destroy();
          } catch (err) {
            toast.error(`Error processing ${file.name}: Invalid PDF file`);
          }
        } else if (file.type.includes('word')) {
            setIsConverting(true);
            const formData = new FormData();
            formData.append('file', file);
            try {
                const response = await fetch(`${BASE_URL}/convert-and-count`, {
                    method: 'POST',
                    body: formData,
                });
                const data = await response.json();
                if (response.ok) {
                    file._pageCount = data.page_count;
                } else {
                    toast.error(data.error || 'Failed to convert file');
                    continue;
                }
            } catch (e) {
                toast.error('Failed to convert file');
                continue;
            } finally {
                setIsConverting(false);
            }
            validFiles.push(file);
        } else {
          file._pageCount = 0; // Non-PDF files have 0 pages
          validFiles.push(file);
        }
      } else {
        toast.error(`Error for ${file.name}: ${error}`);
      }
    }

    const isAnalysisRoute = location.pathname.startsWith('/analysis') || location.pathname === '/';
    const currentFileCount = files.length;
    const uploadedFileCount = userFiles?.files?.length || 0;
    const maxFiles = isAnalysisRoute ? 15 : 10;
    const totalPages = validFiles.reduce((acc, file) => acc + (file._pageCount || 0), 0);
    console.log('[Sidebar][handleFileChange] totalPages:', totalPages, {
      selectedFiles: selectedFiles.map(f => ({ name: f.name, type: f.type })),
      validFiles: validFiles.map(f => ({ name: f.name, pages: f._pageCount })),
      historyId,
      taskID,
    });

    if (isAnalysisRoute && uploadedFileCount + currentFileCount + validFiles.length > maxFiles) {
      const allowedFiles = validFiles.slice(0, maxFiles - uploadedFileCount - currentFileCount);
      const rejectedCount = validFiles.length - allowedFiles.length;
      if (rejectedCount > 0) {
        toast.error(
          `Nur ${maxFiles} Dateien dürfen analysiert werden. Sie haben bereits ${uploadedFileCount} Datei(en) hochgeladen. ${rejectedCount} Datei(en) wurden nicht hinzugefügt.`
        );
      }
      if (allowedFiles.length > 0) {
        setFiles((prevFiles) => [...prevFiles, ...allowedFiles]);
      }
    } else if (totalPages > 0) {
      console.log('[Sidebar][handleFileChange] invoking canUploadPages', {
        org: getUserOrganizationId(userData),
        historyId,
        totalPages,
        taskID: defaultTaskId(taskID),
      });
      const pageCheck = await canUploadPages(
        dispatch,
        getUserOrganizationId(userData),
        historyId,
        totalPages,
        defaultTaskId(taskID)
      );
      console.log('[Sidebar][handleFileChange] canUploadPages result:', pageCheck);
      if (!pageCheck.canProceed) {
        console.warn('[Sidebar][handleFileChange] Upload blocked due to pages limit', pageCheck);
        toast.error(pageCheck.error || 'Upload exceeds available pages');
        return;
      }
      setFiles((prevFiles) => [...prevFiles, ...validFiles]);
    } else {
      setFiles((prevFiles) => [...prevFiles, ...validFiles]);
    }
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(event.dataTransfer.files);
      const validFiles = [];

      for (const file of droppedFiles) {
        const { isValid, error } = validateFile(file);
        if (isValid) {
          if (file.type === 'application/pdf') {
            try {
              const arrayBuffer = await file.arrayBuffer();
              const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
              file._pageCount = pdf.numPages; // Attach page count to file object
              validFiles.push(file);
              await pdf.destroy();
            } catch (err) {
              toast.error(`Error processing ${file.name}: Invalid PDF file`);
            }
          } else if (file.type.includes('word')) {
            setIsConverting(true);
            const formData = new FormData();
            formData.append('file', file);
            try {
                const response = await fetch(`${BASE_URL}/convert-and-count`, {
                    method: 'POST',
                    body: formData,
                });
                const data = await response.json();
                if (response.ok) {
                    file._pageCount = data.page_count;
                } else {
                    toast.error(data.error || 'Failed to convert file');
                    continue;
                }
            } catch (e) {
                toast.error('Failed to convert file');
                continue;
            } finally {
                setIsConverting(false);
            }
            validFiles.push(file);
        } else {
            file._pageCount = 0; // Non-PDF files have 0 pages
            validFiles.push(file);
          }
        } else {
          toast.error(`Error for ${file.name}: ${error}`);
        }
      }

      const isAnalysisRoute = location.pathname.startsWith('/analysis') || location.pathname === '/';
      const currentFileCount = files.length;
      const uploadedFileCount = userFiles?.files?.length || 0;
      const maxFiles = isAnalysisRoute ? 15 : 10;
      const totalPages = validFiles.reduce((acc, file) => acc + (file._pageCount || 0), 0);
      console.log('[Sidebar][handleDrop] totalPages:', totalPages, {
        droppedFiles: droppedFiles.map(f => ({ name: f.name, type: f.type })),
        validFiles: validFiles.map(f => ({ name: f.name, pages: f._pageCount })),
        historyId,
        taskID,
      });

      if (isAnalysisRoute && uploadedFileCount + currentFileCount + validFiles.length > maxFiles) {
        const allowedFiles = validFiles.slice(0, maxFiles - uploadedFileCount - currentFileCount);
        const rejectedCount = validFiles.length - allowedFiles.length;
        if (rejectedCount > 0) {
          toast.error(
            `Nur ${maxFiles} Dateien dürfen analysiert werden. Sie haben bereits ${uploadedFileCount} Datei(en) hochgeladen.`
          );
        }
        if (allowedFiles.length > 0) {
          setFiles((prevFiles) => [...prevFiles, ...allowedFiles]);
        }
      } else if (totalPages > 0) {
        const pageCheck = await canUploadPages(
          dispatch,
          getUserOrganizationId(userData),
          historyId,
          totalPages,
          defaultTaskId(taskID)
        );
        console.log('[Sidebar][handleDrop] canUploadPages result:', pageCheck);
        if (!pageCheck.canProceed) {
          toast.error(pageCheck.error);
          return;
        }
        setFiles((prevFiles) => [...prevFiles, ...validFiles]);
      } else {
        setFiles((prevFiles) => [...prevFiles, ...validFiles]);
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleRemoveFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    const organizationId = getUserOrganizationId(userData);
    if (!organizationId) {
      toast.error('User is not associated with any organization');
      setIsProcessing(false);
      return;
    }

    const isAnalysisRoute = location.pathname.startsWith('/analysis') || location.pathname === '/';
    if (isAnalysisRoute) {
      const totalFileCount = (userFiles?.files?.length || 0) + files.length;
      if (totalFileCount > 15) {
        toast.error(
          `Nur 15 Dateien dürfen analysiert werden. Sie haben bereits ${userFiles?.files?.length || 0} Datei(en) hochgeladen.`
        );
        setIsProcessing(false);
        return;
      }
    }

    setIsProcessing(true);
    setHasPendingFiles(true);

    try {
      let currentHistoryId = historyId;

      if (!currentHistoryId) {
        const taskCheck = await canStartTask(dispatch, organizationId, taskID || '40');
        if (!taskCheck.canProceed) {
          toast.error(taskCheck.error);
          setIsProcessing(false);
          return;
        }

        const historyResult = await createChatHistory(userId, '40');
        currentHistoryId = historyResult.history_id || historyResult.id;
        if (setHistoryId) setHistoryId(currentHistoryId);

        await recordTaskStarted(dispatch, organizationId, taskID || '40');
      }

      const totalPages = files.reduce((acc, file) => acc + (file._pageCount || 0), 0);
      console.log('[Sidebar][handleUpload] totalPages:', totalPages, {
        files: files.map(f => ({ name: f.name, type: f.type, pages: f._pageCount })),
        historyId: currentHistoryId,
        taskID: defaultTaskId(taskID),
      });
      if (totalPages > 0) {
        console.log('[Sidebar][handleUpload] invoking canUploadPages', {
          org: organizationId,
          historyId: currentHistoryId,
          totalPages,
          taskID: defaultTaskId(taskID),
        });
        const pageCheck = await canUploadPages(dispatch, organizationId, currentHistoryId, totalPages, defaultTaskId(taskID));
        console.log('[Sidebar][handleUpload] canUploadPages result:', pageCheck);
        if (!pageCheck.canProceed) {
          console.warn('[Sidebar][handleUpload] Upload blocked due to pages limit', pageCheck);
          toast.error(pageCheck.error || 'Upload exceeds available pages');
          setIsProcessing(false);
          return;
        }
      }

      const page_counts = files.map(f => f._pageCount || 0);
      const uploadResult = await uploadFilesWithHistory(files, userId, taskID || '40', currentHistoryId, page_counts);
      console.log('Upload success:', uploadResult);

      if (totalPages > 0) {
        console.log('[Sidebar][handleUpload] recordPagesUploaded:', { totalPages, historyId: currentHistoryId, taskID: defaultTaskId(taskID) });
        const recorded = await recordPagesUploaded(dispatch, organizationId, currentHistoryId, totalPages, defaultTaskId(taskID));
        if (!recorded) {
          console.warn('[Sidebar][handleUpload] Failed to record page usage');
        }
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setFiles([]);

      dispatch(
        fetchUserFiles({
          userId: userId,
          historyId: currentHistoryId || historyId,
        })
      );

      toast.success('Dateien erfolgreich aktualisiert');
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRefreshFiles = async () => {
    if (!userId || !historyId) {
      toast.error('Cannot refresh files: missing user or history information');
      return;
    }

    setIsRefreshing(true);
    try {
      dispatch(clearUserFiles());
      await dispatch(fetchUserFiles({ userId, historyId }));
      toast.success('Dateien erfolgreich aktualisiert');
    } catch (error) {
      console.error('Error refreshing files:', error);
      toast.error('Failed to refresh files');
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (userId && historyId) {
      dispatch(fetchUserFiles({ userId, historyId }));
    }
  }, [userId, historyId, dispatch]);

  useEffect(() => {
    if (activeTab === 'files' && userId && historyId) {
      if (!userFiles?.files?.length) {
        console.log('Refetching files due to tab switch');
        dispatch(fetchUserFiles({ userId, historyId }));
      }
    }
  }, [activeTab, userId, historyId, dispatch, userFiles?.files?.length]);

  const scrollToActiveQuestion = () => {
    setTimeout(() => {
      if (questionCatalogRef.current) {
        const activeQuestionElement = questionCatalogRef.current.querySelector('[data-active="true"]');
        if (activeQuestionElement) {
          console.log('Found active question, scrolling to:', activeQuestionElement.id);
          activeQuestionElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          const container = document.querySelector('.overflow-y-auto');
          if (container) {
            const elementTop = activeQuestionElement.getBoundingClientRect().top;
            const containerTop = container.getBoundingClientRect().top;
            const offsetPosition = elementTop - containerTop - 100;
            container.scrollBy({
              top: offsetPosition,
              behavior: 'smooth',
            });
          }
        } else {
          console.log('No active question element found to scroll to');
        }
      }
    }, 500);
  };

  const handleClose = () => {
    if (!isPdfFullscreen && !pdfBlobUrl && !currentPdf.blob) {
      // Do nothing regarding sidebar width
    }

    if (isPdfFullscreen) {
      setIsPdfFullscreen(false);
    } else if (pdfBlobUrl || currentPdf.blob) {
      if (mode === 'chat') {
        dispatch(clearSources());
        setPdfBlobUrl(null);
        setActiveTab('files');
        setActiveQuestionId(null);
        localStorage.removeItem('activeQuestionId');
        localStorage.setItem('pdfOpenedFromTab', 'files');
        localStorage.setItem('currentActiveTab', 'files');
      } else {
        const savedTab = localStorage.getItem('pdfOpenedFromTab');
        const sourceMessageId = localStorage.getItem('pdfSourceMessageId');
        const sourceMessageTitle = localStorage.getItem('pdfSourceMessageTitle');

        console.log('Closing PDF, source message title:', sourceMessageTitle);

        if (sourceMessageTitle && questions && questions.length > 0) {
          const matchingQuestion = questions.find(
            (q) => q.title && q.title.toLowerCase() === sourceMessageTitle.toLowerCase()
          );
          if (matchingQuestion) {
            console.log('Found matching question!', matchingQuestion);
            setActiveQuestionId(matchingQuestion.id);
            localStorage.setItem('activeQuestionId', matchingQuestion.id);
          } else {
            console.log('No matching question found by title');
            setActiveQuestionId(sourceMessageId);
          }
        } else if (sourceMessageId) {
          setActiveQuestionId(sourceMessageId);
        }

        dispatch(clearSources());
        setPdfBlobUrl(null);

        if (savedTab) {
          setActiveTab(savedTab);
          console.log('Restoring tab to:', savedTab);
          if (savedTab === 'tasks') {
            scrollToActiveQuestion();
          }
        }
      }

      if (userId && historyId) {
        const taskId = mode === 'chat' ? '40' : taskID;
        dispatch(fetchUserFiles({ userId, taskId, historyId }));
      }
    } else {
      onClose();
    }
  };

  const handleTabChange = (tab) => {
    const previousTab = activeTab;
    setActiveTab(tab);
    localStorage.setItem('pdfOpenedFromTab', tab);
    localStorage.setItem('currentActiveTab', tab);

    if (tab === 'files' || (previousTab === 'files' && tab === 'tasks')) {
      setActiveQuestionId(null);
      localStorage.removeItem('activeQuestionId');
      console.log('Clearing active question when switching tabs');
    } else if (tab === 'tasks' && previousTab === 'files') {
      if (activeQuestionId) {
        scrollToActiveQuestion();
      }
    }

    if (tab === 'files') {
      dispatch(fetchUserFiles({ userId, historyId }));
    }
    if (tab === 'tasks' && taskID && mode !== 'chat') {
      dispatch(fetchSQByTaskId(taskID));
    }
  };

  const handleDeleteFile = async (fileId, filename) => {
    try {
      setDeletingFileId(fileId);
      const response = await fetch(
        `${BASE_URL}/files/${historyId}/${userId}/${taskID}/${filename}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }

      dispatch(fetchUserFiles({ userId, historyId }));
    } catch (error) {
      console.error('Error deleting file:', error);
    } finally {
      setDeletingFileId(null);
    }
  };

  const renderFiles = () => {
    if (userFilesStatus === 'loading' && !userFiles?.files?.length) {
      return <div className="p-3">Loading files...</div>;
    }

    if (userFilesStatus === 'failed') {
      return <div className="p-3 text-red-500">Error: {userFilesError}</div>;
    }

    return userFiles?.files?.map((file) => (
      <div
        key={file.id}
        className="p-3 bg-custom-gray_white flex items-center justify-between w-full rounded-md hover:bg-gray-50"
      >
        <div
          className="flex items-center gap-2 cursor-pointer flex-1"
          onClick={() => {
            localStorage.setItem('pdfOpenedFromTab', 'files');
            localStorage.setItem('currentActiveTab', 'files');
            localStorage.removeItem('activeQuestionId');
            setActiveQuestionId(null);
            dispatch(addSource({ filename: file?.filename, page: 1 }));
            dispatch(
              fetchPDF({
                userId,
                taskId: defaultTaskId(taskID),
                historyId,
                filename: file?.filename,
                page: 1,
              })
            );
          }}
        >
          <h2
            className="text-sm font-medium text-gray-900 truncate"
            style={{ maxWidth: `${Math.max(sidebarWidth - 180, 100)}px` }}
          >
            {file.filename.length > Math.max(Math.floor((sidebarWidth - 180) / 8), 10)
              ? `${file.filename.substring(0, Math.max(Math.floor((sidebarWidth - 180) / 8), 10))}.....`
              : file.filename}
          </h2>
          <BookOpen size={18} className="text-gray-600 hover:text-custom-yellow transition-colors duration-200" />
        </div>
        <div className="flex items-center space-x-4">
          <div
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white shadow-sm transition-colors ${
              file.status === 'done' ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-400 hover:bg-orange-500'
            }`}
          >
            <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-white"></span>
            {file.status === 'done' ? 'Done' : 'Pending'}
          </div>
        </div>
        {file.status === 'done' && (
          <button
            onClick={() => handleDeleteFile(file.id, file.filename)}
            className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            aria-label={`Delete ${file.filename}`}
            disabled={deletingFileId === file.id}
          >
            {deletingFileId === file.id ? (
              <div className="w-5 h-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
            ) : (
              <Trash2Icon className="h-5 w-5" />
            )}
          </button>
        )}
      </div>
    ));
  };

  useEffect(() => {
    let intervalId = null;

    const startPolling = () => {
      if (activeTab === 'files' && userId) {
        intervalId = setInterval(() => {
          const hasPending = userFiles?.files?.some((file) => file.status === 'Pending');
          if (hasPending) {
            dispatch(fetchUserFiles({ userId, historyId }));
          } else {
            if (intervalId) {
              clearInterval(intervalId);
              intervalId = null;
            }
          }
        }, 20000);
      }
    };

    const hasPendingFiles = userFiles?.files?.some((file) => file.status === 'Pending');
    if (hasPendingFiles && activeTab === 'files' && userId) {
      startPolling();
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [userFiles, activeTab, userId, dispatch]);

  const handleDescriptionSelect = (question) => {
    if (!question || !question.title) {
      console.warn('Invalid question object:', question);
      return;
    }
    const sanitizedTitle = sanitizeTitleForId(question.title);
    const chatMsgElem = document.getElementById(`chat-message-${sanitizedTitle}`);
    if (chatMsgElem) {
      chatMsgElem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      flashTitleElemWhenVisible(sanitizedTitle);
      return;
    }
    onDescriptionSelect(question);
  };

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    document.body.style.cursor = 'ew-resize';
    document.body.classList.add('resize-active');
  }, []);

  const handleMouseMove = (e) => {
    const newWidth = window.innerWidth - e.clientX;
    const maxWidth = window.innerWidth * 0.4;
    const minWidth = 445;

    if (newWidth >= minWidth && newWidth <= maxWidth) {
      const roundedWidth = Math.round(newWidth);
      localStorage.setItem('sidebarWidth', roundedWidth);
      setSidebarWidth(roundedWidth);

      if (onWidthChange) {
        onWidthChange(roundedWidth);
      }

      const newZoomLevel = 1.2 - ((newWidth - minWidth) / (maxWidth - minWidth)) * 0.4;
      setPdfZoomLevel(newZoomLevel);

      if (pdfBlobUrl || currentPdf.blob) {
        window.dispatchEvent(new Event('resize'));
      }

      const sidebarContainer = document.querySelector('.h-full.transition-all.duration-300');
      if (sidebarContainer) {
        sidebarContainer.style.transition = 'width 0.05s ease';
        sidebarContainer.style.width = `${newWidth}px`;
      }
    }
  };

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    document.body.style.cursor = '';
    document.body.classList.remove('resize-active');
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const defaultTaskId = (taskId) => (!taskId || taskId === 'null') ? '40' : taskId;

  useEffect(() => {
    dispatch(clearSources());
    setPdfBlobUrl(null);
    setIsPdfFullscreen(false);
  }, [location.pathname]);

  return (
    <div
      ref={sidebarRef}
      style={{
        width: isOpen && !isPdfFullscreen ? `${sidebarWidth}px` : isPdfFullscreen ? '0' : '0',
      }}
      className={`h-[calc(100vh-50px)] w-[4px] fixed right-0 bg-white transition-property-opacity transition-duration-300 z-40 ${
        isPdfFullscreen || !isOpen ? 'overflow-hidden' : ''
      } flex flex-col`}
    >
      {isOpen && !isPdfFullscreen && (
        <div
          ref={resizeHandleRef}
          className="absolute left-0 top-0 h-full cursor-ew-resize z-40 group flex items-center justify-center"
          onMouseDown={handleMouseDown}
        >
          <div className="w-1 h-fit bg-white transition-colors duration-200"></div>
          {isDragging && <div className="fixed inset-0 z-40" />}
        </div>
      )}
      <div className="border">
        <div className="p-3 flex justify-between z-70">
          {loading || pdfBlobUrl ? (
            <div className="flex items-center gap-2 group">
              <h1 className="font-medium p-3 truncate max-w-[300px] transition-colors duration-200" title={currentPdf.filename}>
                {currentPdf.filename}
              </h1>
              <div className="absolute invisible group-hover:visible bg-gray-200 text-black text-sm rounded px-2 py-1 mt-12 whitespace-nowrap z-50 shadow-lg">
                {currentPdf.filename}
              </div>
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-custom-yellow border-t-transparent" />
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-4 w-full">
              <div className="flex gap-2 mt-2">
                {mode !== 'chat' && (
                  <Button
                    onClick={() => handleTabChange('tasks')}
                    icon={<TaskIcon fill={activeTab === 'tasks' ? aispaceColor : '#5F6368'} />}
                    text="Checkliste"
                    rounded={true}
                    className={activeTab === 'tasks' ? 'nav-btn-selected' : 'nav-btn-unselected'}
                    iconClassName={activeTab === 'tasks' ? 'navbar-icon-selected' : 'navbar-icon'}
                    textClassName="navbar-text"
                    style={{ marginRight: '6px' }}
                  />
                )}
                <Button
                  onClick={() => handleTabChange('files')}
                  icon={<FileIcon fill={activeTab === 'files' ? aispaceColor : '#5F6368'} />}
                  text="Dateien"
                  rounded={true}
                  className={activeTab === 'files' ? 'nav-btn-selected' : 'nav-btn-unselected'}
                  iconClassName={activeTab === 'files' ? 'navbar-icon-selected' : 'navbar-icon'}
                  textClassName="navbar-text"
                  style={{ marginRight: '6px' }}
                />
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefreshFiles}
              disabled={isRefreshing}
              className="text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Refresh files"
              title="Refresh files"
            >
              <img
                src={require('../../assets/refresh.png')}
                alt="Refresh"
                className={`w-7 h-6 object-contain ${isRefreshing ? 'animate-spin' : ''}`}
              />
            </button>
            <button onClick={handleClose} className="text-gray-500 hover:text-gray-700" aria-label="Close sidebar">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto shadow-md">
        {loading || pdfBlobUrl ? (
          <>
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-custom-yellow border-t-transparent" />
                  <p className="text-gray-600">Loading PDF...</p>
                </div>
              </div>
            ) : (
              <div className="w-full h-full" style={{ width: `${sidebarWidth}px` }}>
                <PDFViewer
                  file={currentPdf.blob}
                  initialPage={currentPdf.page}
                  zoomLevel={pdfZoomLevel}
                  onFullscreenChange={(isFullscreen) => setIsPdfFullscreen(isFullscreen)}
                />
              </div>
            )}
          </>
        ) : (
          <div className="w-full p-4 flex flex-col gap-4">
            {activeTab === 'tasks' && (
              <div className="flex flex-col gap-4 mt-4" ref={questionCatalogRef}>
                <div className="flex flex-col gap-4">
                  {(() => {
                    const savedQuestionId = localStorage.getItem('activeQuestionId');
                    const sourceTitle = localStorage.getItem('pdfSourceMessageTitle');
                    let foundExactTitleMatch = false;
                    let foundIdMatch = false;

                    console.log('Searching for question match with:', {
                      savedQuestionId,
                      sourceTitle,
                      questionCount: questions.length,
                    });

                    if (questions.length === 0 || activeTab === 'files') {
                      return [];
                    }

                    if (sourceTitle) {
                      console.log('Available questions to match against:');
                      questions.forEach((q) => {
                        console.log(`- "${q.title}" (ID: ${q.id})`);
                      });
                    }

                    return questions.map((question, index) => {
                      const exactTitleMatch =
                        sourceTitle &&
                        question.title &&
                        sourceTitle.toLowerCase().trim() === question.title.toLowerCase().trim();
                      const idMatch =
                        savedQuestionId &&
                        question.id &&
                        savedQuestionId.toString() === question.id.toString();
                      const partialTitleMatch =
                        !exactTitleMatch &&
                        sourceTitle &&
                        question.title &&
                        (sourceTitle.toLowerCase().includes(question.title.toLowerCase()) ||
                          question.title.toLowerCase().includes(sourceTitle.toLowerCase()));
                      const isActive =
                        exactTitleMatch || (idMatch && !foundExactTitleMatch) || (partialTitleMatch && !foundExactTitleMatch && !foundIdMatch);

                      if (exactTitleMatch) foundExactTitleMatch = true;
                      if (idMatch) foundIdMatch = true;

                      if (isActive) {
                        console.log(
                          'Highlighting question:',
                          question.title,
                          exactTitleMatch ? '(exact title match)' : idMatch ? '(id match)' : '(partial match)'
                        );
                      }

                      return (
                        <SQ
                          key={question.id}
                          question={question}
                          index={index}
                          onDescriptionSelect={handleDescriptionSelect}
                          historyId={historyId}
                          isActive={isActive}
                        />
                      );
                    });
                  })()}
                </div>
              </div>
            )}
            {activeTab === 'files' && (
              <div className="flex flex-col gap-4 files-content">
                <div className="flex flex-col gap-2">
                  <div
                    className="border-2 border-solid border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50"
                    onClick={handleFileClick}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                  >
                    <div className="flex flex-col pt-10 pb-10 items-center justify-center">
                      <svg
                        className="h-10 w-10 text-gray-400 mb-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <p className="text-sm font-medium text-gray-700">Ziehen und legen Sie Ihre Dateien hier ab</p>
                      <p className="text-xs text-gray-500 mt-1">Unterstützt PDF- und Word-Dateien</p>
                    </div>
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx"
                    multiple
                    style={{ display: 'none' }}
                  />
                  {(location.pathname.startsWith('/analysis') || location.pathname === '/') && (
                    <div className="text-xs text-gray-500 text-center">
                      Maximal 15 Dateien für die Analyse zulässig ({(userFiles?.files?.length || 0) + files.length}/15)
                    </div>
                  )}
                </div>
                {files.length > 0 && (
                  <div className="mt-2">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Ausgewählte Dateien:</span>
                      {(location.pathname.startsWith('/analysis') || location.pathname === '/') && (
                        <span className="text-xs text-gray-500">
                          {(userFiles?.files?.length || 0) + files.length}/15
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {files.map((file, index) => (
                        <div key={index} className="flex items-center bg-gray-100 rounded p-2">
                          <span className="text-xs truncate max-w-[150px]">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(index)}
                            className="ml-2 text-gray-500 hover:text-red-500"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                    {files.some((file) => file.type === 'application/pdf') && (
                      <div className="text-xs text-gray-500 mt-2">
                        Gesamtzahl der Seiten in ausgewählten PDF-Dateien: {files.reduce((acc, file) => acc + (file._pageCount || 0), 0)}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={handleUpload}
                      className="mt-3 w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-custom-yellow hover:bg-custom-yellow-dark"
                      disabled={isProcessing || isConverting}
                    >
                      {isConverting ? "Zu PDF konvertieren..." : isProcessing ? 'Hochladen...' : 'Dateien hinzufügen'}
                    </button>
                  </div>
                )}
                <div className="flex flex-col gap-3 mt-4">{renderFiles()}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;